import { useLocation, useNavigate } from "react-router-dom";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import queryString from "query-string";
import { toast } from "react-toastify";

import api from "libs/api";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const DashboardContext = createContext();

const DashboardProvider = ({ children }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const filters = queryString.parse(search);

  const [init_loading, setInitLoading] = useState(true);
  const [requiring_action_data, setRequiringActionData] = useState([]);

  const {
    isLoading: loading,
    data,
    refetch: refetchTasks,
    status,
  } = useQuery({
    queryKey: ["admin_dashboard", filters],
    queryFn: () =>
      api.get(
        `/admin/dashboard?${queryString.stringify({
          ...filters,
          sort: filters?.sort_deadline || "due_date",
        })}`,
      ),
  });

  useEffect(() => {
    if (init_loading) {
      if (status === "success") {
        setRequiringActionData(data?.requiring_action_data);
        setInitLoading(false);
      }
    }
  }, [init_loading, data, status]);

  const filterHandler = (name, value) => {
    navigate(
      `?${queryString.stringify({
        ...filters,
        [name]: value || undefined,
      })}`,
    );
  };

  const updateTask = (id, values) =>
    new Promise(async (resolve, reject) => {
      const taskQueries = queryClient.getQueriesData({
        queryKey: ["admin_dashboard"],
        predicate: (query) => query.queryKey[0] === "admin_dashboard",
      });

      const previousQueryStates = new Map();

      try {
        taskQueries.forEach(([queryKey, queryData]) => {
          if (!queryData?.tasks_upcoming_date) return;

          previousQueryStates.set(queryKey, { ...queryData });

          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData?.tasks_upcoming_date) return oldData;

            const queryFilters = queryKey[1] || {};

            return {
              ...oldData,
              tasks_upcoming_date: oldData.tasks_upcoming_date
                .map((task) => {
                  if (task._id === id) {
                    const updatedTask = { ...task, ...values };

                    if (
                      updatedTask.status &&
                      ![2, 3, 4].includes(updatedTask.status)
                    ) {
                      return null;
                    }

                    if (queryFilters.status) {
                      const statusValue = task_status_data?.find(
                        (item) => item?.key === queryFilters.status,
                      )?.value;

                      if (statusValue && updatedTask.status !== statusValue) {
                        return null;
                      }
                    }

                    if (queryFilters.type) {
                      const typeValue = task_priority_data?.find(
                        (item) => item?.key === queryFilters.type,
                      )?.value;

                      if (typeValue && updatedTask.priority !== typeValue) {
                        return null;
                      }
                    }

                    return updatedTask;
                  }

                  return task;
                })
                .filter(Boolean),
            };
          });
        });

        await api.put(`/admin/tasks/${id}`, values);

        await queryClient.invalidateQueries({
          queryKey: ["admin_dashboard"],
        });

        resolve();
      } catch (err) {
        taskQueries.forEach(([queryKey]) => {
          const originalData = previousQueryStates.get(queryKey);
          if (originalData) {
            queryClient.setQueryData(queryKey, originalData);
          }
        });

        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const value = useMemo(() => {
    return {
      data,
      loading,
      filters,
      filterHandler,
      updateTask,
      requiring_action_data,
      init_loading,
      refetchTasks,
    };
    // eslint-disable-next-line
  }, [data, loading, init_loading, filters]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

const useDashboard = () => useContext(DashboardContext);
export { DashboardContext, useDashboard };
export default DashboardProvider;
